<section class="card card-padding" aria-label="Project papers">
  <!-- Read-only banner: shown above the list whenever the project has moved
       past INGESTION. The corpus is locked at that point; row actions stay
       hidden (the `readonly` input passed into `<app-paper-row>` enforces
       that). -->
  @if (!isIngestionOpen()) {
    <div
      class="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
      data-testid="paper-list-readonly-banner"
    >
      Ingestion is closed for this project. The corpus is read-only.
    </div>
  }

  <!-- Filter bar -->
  <div
    class="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end md:gap-3"
    data-testid="filter-bar"
  >
    <!-- Search input: full-width on mobile, grows on desktop -->
    <div class="flex flex-1 flex-col gap-1 md:min-w-[16rem]">
      <label class="input-label" for="paper-search">Search</label>
      <input
        id="paper-search"
        type="search"
        class="input-field"
        placeholder="Title, author, DOI…"
        [value]="currentFilter().q ?? ''"
        (change)="onSearchChange($any($event.target).value)"
        data-testid="filter-search"
      />
    </div>

    <!-- Source dropdown -->
    <div class="flex flex-col gap-1 md:w-44">
      <label class="input-label" for="paper-source">Source</label>
      <select
        id="paper-source"
        class="select-field"
        [value]="currentSourceValue()"
        (change)="onSourceChange($any($event.target).value)"
        data-testid="filter-source"
      >
        @for (opt of sourceOptions; track opt.value) {
          <option [value]="opt.value">{{ opt.label }}</option>
        }
      </select>
    </div>

    <!-- Work-type dropdown -->
    <div class="flex flex-col gap-1 md:w-44">
      <label class="input-label" for="paper-work-type">Work type</label>
      <select
        id="paper-work-type"
        class="select-field"
        [value]="currentWorkTypeValue()"
        (change)="onWorkTypeChange($any($event.target).value)"
        data-testid="filter-work-type"
      >
        @for (opt of workTypeOptions; track opt.value) {
          <option [value]="opt.value">{{ opt.label }}</option>
        }
      </select>
    </div>

    <!-- Venue-type dropdown -->
    <div class="flex flex-col gap-1 md:w-44">
      <label class="input-label" for="paper-source-type">Venue type</label>
      <select
        id="paper-source-type"
        class="select-field"
        [value]="currentSourceTypeValue()"
        (change)="onSourceTypeChange($any($event.target).value)"
        data-testid="filter-source-type"
      >
        @for (opt of sourceTypeOptions; track opt.value) {
          <option [value]="opt.value">{{ opt.label }}</option>
        }
      </select>
    </div>

    <!-- Needs-attention toggle -->
    <div class="flex flex-col gap-1">
      <span class="input-label">Status</span>
      <button
        type="button"
        (click)="toggleNeedsAttention()"
        class="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
        [class.border-amber-300]="needsAttentionActive()"
        [class.bg-amber-50]="needsAttentionActive()"
        [class.text-amber-800]="needsAttentionActive()"
        [class.border-slate-200]="!needsAttentionActive()"
        [class.bg-white]="!needsAttentionActive()"
        [class.text-slate-700]="!needsAttentionActive()"
        [attr.aria-pressed]="needsAttentionActive()"
        data-testid="filter-needs-attention"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          class="h-4 w-4"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
          />
        </svg>
        Needs attention
      </button>
    </div>

    <!-- Sort dropdown -->
    <div class="flex flex-col gap-1 md:w-48">
      <label class="input-label" for="paper-sort">Sort</label>
      <select
        id="paper-sort"
        class="select-field"
        [value]="currentSortValue()"
        (change)="onSortChange($any($event.target).value)"
        data-testid="filter-sort"
      >
        @for (opt of sortOptions; track opt.value) {
          <option [value]="opt.value">{{ opt.label }}</option>
        }
      </select>
    </div>

    <!-- Page-size dropdown -->
    <div class="flex flex-col gap-1 md:w-32">
      <label class="input-label" for="paper-page-size">Per page</label>
      <select
        id="paper-page-size"
        class="select-field"
        [value]="currentPageReq().size"
        (change)="onPageSizeChange($any($event.target).value)"
        data-testid="filter-page-size"
      >
        @for (size of pageSizeOptions; track size) {
          <option [value]="size">{{ size }}</option>
        }
      </select>
    </div>
  </div>

  <!-- Body: empty states or rows -->
  <div class="mt-4">
    @switch (emptyState()) {
      @case ('loading') {
        <div class="flex flex-col gap-2 md:gap-0" data-testid="paper-list-loading">
          @for (_ of [1, 2, 3, 4, 5]; track $index) {
            <div
              class="skeleton-row flex animate-pulse items-center gap-4 rounded-lg border border-slate-200 bg-white p-3 md:rounded-none md:border-x-0 md:border-t-0 md:border-b md:p-2"
            >
              <div class="hidden h-6 w-24 rounded-md bg-slate-200 skeleton-shimmer md:block"></div>
              <div class="min-w-0 flex-1 space-y-2">
                <div class="h-4 w-3/5 rounded bg-slate-200 skeleton-shimmer"></div>
                <div class="h-3 w-2/5 rounded bg-slate-200 skeleton-shimmer"></div>
                <div class="flex gap-2">
                  <div class="h-5 w-16 rounded-full bg-slate-200 skeleton-shimmer"></div>
                  <div class="h-5 w-12 rounded bg-slate-200 skeleton-shimmer"></div>
                  <div class="h-5 w-14 rounded bg-slate-200 skeleton-shimmer"></div>
                </div>
              </div>
              <div class="hidden h-8 w-20 rounded-md bg-slate-200 skeleton-shimmer md:block"></div>
            </div>
          }
        </div>
      }
      @case ('no-papers') {
        <div
          class="flex flex-col items-center justify-center gap-2 py-12 text-center"
          data-testid="paper-list-empty"
        >
          <p class="text-base font-semibold text-gray-700">No papers yet</p>
          <p class="text-sm text-gray-500">
            Use “+ Add papers” above to bring papers into this project.
          </p>
        </div>
      }
      @case ('no-matches') {
        <div
          class="flex flex-col items-center justify-center gap-2 py-12 text-center"
          data-testid="paper-list-no-matches"
        >
          <p class="text-base font-semibold text-gray-700">No papers match this filter</p>
          <button
            type="button"
            (click)="resetFilter()"
            class="text-sm font-semibold text-secondary hover:text-hover"
            data-testid="paper-list-reset-filter"
          >
            Reset filters
          </button>
        </div>
      }
      @case ('closed') {
        <div
          class="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center"
          data-testid="paper-list-closed"
        >
          <p class="text-sm text-gray-700">
            Ingestion has been finalized. The corpus is read-only.
          </p>
        </div>
      }
      @default {
        <ul class="flex flex-col gap-2 md:gap-0 md:divide-y md:divide-slate-200">
          @for (paper of currentPapers()?.items ?? []; track paper.id) {
            <li>
              <app-paper-row
                [paper]="paper"
                [readonly]="rowsReadonly()"
                [importRunning]="runningImportJobIdSet().has(paper.importJobId)"
                (edit)="onEdit($event)"
                (attach)="onAttach($event)"
                (viewDetails)="onViewDetails($event)"
                (viewAuditTrail)="onViewAuditTrail($event)"
              ></app-paper-row>
            </li>
          }
        </ul>
      }
    }
  </div>

  <!-- Pagination footer -->
  @if (currentPapers(); as page) {
    @if (page.items.length > 0) {
      <div
        class="mt-4 flex flex-col items-center gap-3 border-t border-slate-200 pt-3 text-sm text-gray-600 md:flex-row md:justify-between"
        data-testid="paper-list-pagination"
      >
        <span>
          Page {{ currentPageIndex() + 1 }} of {{ totalPages() || 1 }} · {{ totalElements() }} total
        </span>
        <div class="flex items-center gap-2">
          <button
            type="button"
            (click)="goPrev()"
            [disabled]="!canPrev()"
            class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="paper-list-prev"
          >
            « Prev
          </button>
          <button
            type="button"
            (click)="goNext()"
            [disabled]="!canNext()"
            class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="paper-list-next"
          >
            Next »
          </button>
        </div>
      </div>
    }
  }
</section>
